// ── 연차 요약 섹션 (모바일 아코디언 카드 + 데스크탑 테이블) ────────────────────
import React, { useState } from 'react';
import { toDateString } from '../../../utils/date';
import AdminColHeader from '../../../components/AdminColHeader';
import { remainingCellStyle, companyBadgeStyle, LEAVE_FILTER_DEFS, matchesLeaveFilter, badgeStyle } from './utils';
import { TYPE_BADGE } from './constants';
// 표 — 직원 관리 기준 디자인 표준 (2026-08-26 통일)
import { stdTableWrap, stdTable, stdTh, stdCell } from '../adminlayout/designStandard';
import {
  sectionTitleStyle,
  trHoverStyle, emptyTdStyle, emptyTextStyle,
  summaryCardStyle,
} from './stylesA';

// 헤더 레이블 → 타입 필터 key 매핑 ('잔여'는 null — 클릭 불가)
const HEADER_FILTER_KEY = { 생성: 'generated', 사용: 'used', 정산: 'settled', 잔여: null, 유급인정: 'paid_recognized', 무급: 'unpaid' };
const FILTER_COLOR_MAP = LEAVE_FILTER_DEFS.reduce((acc, f) => { acc[f.key] = f.color; return acc; }, {});

// hex 색상을 rgba 15%로 변환 (CSS 변수는 그대로 반환)
function tintBg(color) {
  if (!color || !color.startsWith('#')) return 'var(--bg-subtle)';
  const h = color.slice(1);
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},0.15)`;
}

const MOBILE_PAGE_SIZE = 10;

// 모바일 stat 키 → 필터키/색 매핑 (0 이상만 표시)
const STAT_DEFS = [
  { key: 'generated', label: '생성',   field: 'generated', color: '#1d4ed8' },
  { key: 'used',      label: '사용',   field: 'used',      color: '#0f766e' },
  { key: 'settled',   label: '정산',   field: 'settled',   color: '#7c3aed' },
  { key: 'paid_recognized', label: '유급인정', field: 'paid_recognized', color: '#2563eb' },
  { key: 'unpaid',    label: '무급',   field: 'unpaid',    color: '#9a3412' },
];

export function SummarySection({ summaryRows, isMobile, selectedName, onRowClick, activeTypeFilters, onToggleTypeFilter, leaves, onEditLeave }) {
  // 행 클릭 토글 (같은 이름 클릭 시 해제)
  const handleClick = (name) => {
    if (!onRowClick) return;
    onRowClick(selectedName === name ? '' : name);
  };
  const isSelected = (name) => !!name && selectedName === name;
  const typeFilters = Array.isArray(activeTypeFilters) ? activeTypeFilters : [];
  const isFilterActive = (key) => !!key && typeFilters.includes(key);
  const handleHeaderClick = (label, e) => {
    const key = HEADER_FILTER_KEY[label];
    if (!key || !onToggleTypeFilter) return;
    e.stopPropagation();
    onToggleTypeFilter(key);
  };

  // 모바일 아코디언 상태 (한 번에 하나)
  const [expandedId, setExpandedId] = useState(null);
  const [filtersPerUser, setFiltersPerUser] = useState({});
  const [pagePerUser, setPagePerUser] = useState({});
  const toggleExpand = (uid) => {
    if (expandedId === uid) { setExpandedId(null); return; }
    setExpandedId(uid);
    setFiltersPerUser({}); // 이전 직원 필터 리셋
    setPagePerUser({});
  };
  const toggleStatFilter = (uid, key, e) => {
    e.stopPropagation();
    setFiltersPerUser((prev) => {
      const cur = prev[uid] || [];
      return { ...prev, [uid]: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key] };
    });
    setPagePerUser((prev) => ({ ...prev, [uid]: 1 }));
  };
  return (
    // alignContent start: 여유 공간을 아래쪽으로 고정(자식 간 분산 방지) → 흔들림 방지 유지
    // 카드 박스 제거 — 표준(직원 관리)은 표 자체가 카드 (2026-08-26 통일)
    <div style={{ display: 'grid', gap: 8, alignContent: 'start' }}>
      <div style={sectionTitleStyle}>연차 요약</div>

      {isMobile ? (
        // ── 모바일: 아코디언 카드 (탭 → 펼침, 숫자 클릭 → 필터, 내부 연차 리스트) ──
        <div style={{ display: 'grid', gap: 4 }}>
          {summaryRows.length === 0 && (
            <div style={emptyTextStyle}>데이터가 없습니다.</div>
          )}
          {summaryRows.map((row) => {
            const uid = row.user_id;
            const expanded = expandedId === uid;
            const userFilters = filtersPerUser[uid] || [];
            const userLeaves = (leaves || []).filter((l) => Number(l.user_id) === Number(uid));
            // 합계가 0이어도 해당 유형 내역이 있으면 노출합니다. 예: 월차 생성 0일.
            const stats = STAT_DEFS.filter((s) =>
              Number(row[s.field] || 0) > 0 || userLeaves.some((l) => matchesLeaveFilter(l, s.key))
            );
            const filteredLeaves = userFilters.length > 0
              ? userLeaves.filter((l) => userFilters.some((k) => matchesLeaveFilter(l, k)))
              : userLeaves;
            const page = pagePerUser[uid] || 1;
            const totalPages = Math.max(1, Math.ceil(filteredLeaves.length / MOBILE_PAGE_SIZE));
            const pagedLeaves = filteredLeaves.slice((page - 1) * MOBILE_PAGE_SIZE, page * MOBILE_PAGE_SIZE);
            return (
              <article
                key={`sm-${uid}`}
                style={{
                  ...summaryCardStyle, padding: '8px 10px', gap: 4,
                  ...(isSelected(row.name) ? { background: 'rgba(99,102,241,0.12)', borderColor: '#6366f1' } : null),
                }}
              >
                {/* 1행: 이름 + 회사뱃지 + 잔여 (오른쪽) — 탭 → 아코디언 토글 */}
                <div onClick={() => toggleExpand(uid)} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <strong style={{ fontSize: 13, fontWeight: 600 }}>{row.name}</strong>
                  {row.company_name && <span style={companyBadgeStyle(row.company_name)}>{row.company_name}</span>}
                  <span style={{ ...remainingCellStyle(row.remaining), marginLeft: 'auto' }}>
                    잔여 {Number(row.remaining || 0)}
                  </span>
                </div>
                {/* 2행: 숫자 필터 버튼 (0 숨김, 클릭 다중 토글) + 펼침 인디케이터 */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, fontSize: 11, alignItems: 'center' }}>
                  {stats.map((s, i) => {
                    const active = userFilters.includes(s.key);
                    return (
                      <React.Fragment key={s.key}>
                        {i > 0 && <span style={{ color: 'var(--border)' }}>·</span>}
                        <span
                          onClick={(e) => toggleStatFilter(uid, s.key, e)}
                          style={{
                            cursor: 'pointer', padding: '1px 6px', borderRadius: 4,
                            ...(active
                              ? { background: tintBg(s.color), color: s.color, fontWeight: 700 }
                              : { color: 'var(--text-sub)' }),
                          }}
                        >
                          {s.label} {Number(row[s.field] || 0)}
                        </span>
                      </React.Fragment>
                    );
                  })}
                  <span onClick={() => toggleExpand(uid)} style={{ marginLeft: 'auto', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 14 }}>
                    {expanded ? '▲' : '▼'}
                  </span>
                </div>
                {/* 펼침: 내부 연차 리스트 + 페이지네이션 */}
                {expanded && (
                  <div style={{ marginTop: 4, paddingTop: 6, borderTop: '1px solid var(--border)', display: 'grid', gap: 4 }}>
                    {pagedLeaves.length === 0 && (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: 8 }}>데이터가 없습니다.</div>
                    )}
                    {pagedLeaves.map((lv) => (
                      <div
                        key={`l-${lv.id}`}
                        onClick={() => onEditLeave && onEditLeave(lv)}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px', borderRadius: 6, cursor: 'pointer', background: 'var(--bg-subtle)' }}
                      >
                        <span style={{ width: 50, fontSize: 12, color: 'var(--text-sub)' }}>{String(toDateString(lv.date) || '').slice(5)}</span>
                        <span style={{ width: 60 }}><span style={badgeStyle(TYPE_BADGE, lv.type)}>{lv.type}</span></span>
                        <span style={{ width: 30, fontSize: 13 }}>{lv.count}</span>
                        <span style={{ marginLeft: 'auto', color: 'var(--text-muted)' }}>›</span>
                      </div>
                    ))}
                    {totalPages > 1 && (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, paddingTop: 4, fontSize: 12 }}>
                        <button
                          disabled={page <= 1}
                          onClick={(e) => { e.stopPropagation(); setPagePerUser((p) => ({ ...p, [uid]: Math.max(1, page - 1) })); }}
                          style={{ padding: '2px 10px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg-card)', cursor: 'pointer' }}
                        >이전</button>
                        <span style={{ color: 'var(--text-muted)' }}>{page} / {totalPages}</span>
                        <button
                          disabled={page >= totalPages}
                          onClick={(e) => { e.stopPropagation(); setPagePerUser((p) => ({ ...p, [uid]: Math.min(totalPages, page + 1) })); }}
                          style={{ padding: '2px 10px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg-card)', cursor: 'pointer' }}
                        >다음</button>
                      </div>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      ) : (
        // ── 데스크탑: 테이블 ────────────────────────────────────────────────
        <div style={stdTableWrap}>
          <table style={{ ...stdTable, tableLayout: 'fixed', minWidth: 840 }}>
            <thead>
              <tr>
                {/* 성명·입사일자는 왼쪽 정렬, 숫자 컬럼은 가운데 정렬 */}
                {[
                  { label: '성명',    align: 'left',   db: 'leaves.user_id → users.name' },
                  { label: '입사일자', align: 'left',   db: 'users.hire_date' },
                  { label: '생성',    align: 'center', db: 'leaves (computed: SUM count WHERE category=생성)' },
                  { label: '사용',    align: 'center', db: 'leaves (computed: SUM count WHERE type IN 연차/반차/경조사)' },
                  { label: '정산',    align: 'center', db: 'leaves (computed: SUM count WHERE category=정산)' },
                  { label: '잔여',    align: 'center', db: 'leaves (computed: 생성 - 사용 - 정산)' },
                  { label: '유급인정', align: 'center', db: 'leaves (computed: payroll_effect=paid_cover AND leave_balance_effect=none)' },
                  { label: '무급',    align: 'center', db: 'leaves (computed: SUM count WHERE category=무급)' },
                ].map(({ label, align, db }) => {
                  const fkey = HEADER_FILTER_KEY[label];
                  const clickable = !!fkey && !!onToggleTypeFilter;
                  const active = isFilterActive(fkey);
                  const color = FILTER_COLOR_MAP[fkey];
                  return (
                    <th
                      key={label}
                      onClick={clickable ? (e) => handleHeaderClick(label, e) : undefined}
                      style={{
                        ...stdTh, textAlign: align,
                        cursor: clickable ? 'pointer' : 'default',
                        ...(active ? { background: tintBg(color), color, borderBottom: `2px solid ${color}` } : null),
                      }}
                    >
                      <AdminColHeader label={label} db={db} />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {summaryRows.length === 0 && (
                <tr><td colSpan="8" style={emptyTdStyle}>데이터가 없습니다.</td></tr>
              )}
              {summaryRows.map((row, rowIndex) => {
                const cellStyle = stdCell(rowIndex, summaryRows.length); // 마지막 행 밑줄 제거 (표준)
                return (
                <tr
                  key={`s-${row.user_id}`}
                  onClick={() => handleClick(row.name)}
                  style={{
                    ...trHoverStyle,
                    cursor: onRowClick ? 'pointer' : 'default',
                    ...(isSelected(row.name) ? { background: 'rgba(99,102,241,0.12)', boxShadow: 'inset 3px 0 0 #6366f1' } : null),
                  }}
                >
                  <td style={cellStyle}>
                    <strong>{row.name}</strong>
                    {/* 통합 표시 시 외부 회사 배지 */}
                    {row.company_name && (
                      <span style={{ ...companyBadgeStyle(row.company_name), marginLeft: 5 }}>{row.company_name}</span>
                    )}
                  </td>
                  <td style={cellStyle}>{toDateString(row.hire_date || '') || '-'}</td>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>{Number(row.generated || 0)}</td>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>{Number(row.used || 0)}</td>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>{Number(row.settled || 0)}</td>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>
                    <span style={remainingCellStyle(row.remaining)}>
                      {Number(row.remaining || 0)}
                    </span>
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>{Number(row.paid_recognized || 0)}</td>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>{Number(row.unpaid || 0)}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
